import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save, Trash2, ArrowLeft, Loader2 } from 'lucide-react';
import { api } from '../api/client';
import FormField from '../components/FormField';
import LoadingSpinner from '../components/LoadingSpinner';
import { useEmitenteAtivo } from '../context/EmitenteAtivoContext';

interface Veiculo {
  id?: number;
  emitente_id: number;
  placa: string;
  renavam?: string | null;
  tara?: number | null;
  cap_kg?: number | null;
  cap_m3?: number | null;
  tipo_rodado?: number | null;
  tipo_carroceria?: number | null;
  uf?: string | null;
  descricao?: string | null;
  ativo?: boolean;
}

const emptyVeiculo = (emitId?: number): Veiculo => ({
  emitente_id: emitId ?? 0,
  placa: '',
  renavam: '',
  tara: null,
  cap_kg: null,
  cap_m3: null,
  tipo_rodado: null,
  tipo_carroceria: null,
  uf: '',
  descricao: '',
  ativo: true,
});

const TIPO_RODADO = [
  { v: 1, l: '01 - Truck' },
  { v: 2, l: '02 - Toco' },
  { v: 3, l: '03 - Cavalo Mecânico' },
  { v: 4, l: '04 - VAN' },
  { v: 5, l: '05 - Utilitário' },
  { v: 6, l: '06 - Outros' },
];

const TIPO_CARROCERIA = [
  { v: 0, l: '00 - Não Aplicável' },
  { v: 1, l: '01 - Aberta' },
  { v: 2, l: '02 - Fechada/Baú' },
  { v: 3, l: '03 - Granelera' },
  { v: 4, l: '04 - Porta Container' },
  { v: 5, l: '05 - Sider' },
];

export default function VeiculoEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [search] = useSearchParams();
  const emitFromQuery = search.get('emitente') ? Number(search.get('emitente')) : undefined;

  const isNew = !id || id === 'novo';
  const vid = isNew ? null : Number(id);
  const { emitenteId } = useEmitenteAtivo();

  const q = useQuery({
    queryKey: ['veiculo', vid],
    queryFn: async () => (await api.get<Veiculo>(`/veiculos/${vid}`)).data,
    enabled: !isNew && !!vid,
  });

  const [form, setForm] = useState<Veiculo>(emptyVeiculo(emitFromQuery));

  useEffect(() => {
    if (q.data) setForm({ ...q.data });
  }, [q.data]);

  useEffect(() => {
    if (isNew && emitenteId && !form.emitente_id) {
      setForm((s) => ({ ...s, emitente_id: emitenteId }));
    }
  }, [isNew, emitenteId, form.emitente_id]);

  const set = <K extends keyof Veiculo>(k: K, v: Veiculo[K]) =>
    setForm((s) => ({ ...s, [k]: v }));

  const saveMut = useMutation({
    mutationFn: async (v: Veiculo) => {
      if (isNew) {
        const { data } = await api.post<Veiculo>('/veiculos', v);
        return data;
      }
      const { data } = await api.patch<Veiculo>(`/veiculos/${vid}`, v);
      return data;
    },
    onSuccess: (v) => {
      qc.invalidateQueries({ queryKey: ['veiculos'] });
      navigate(`/veiculos${v.emitente_id ? `?emitente=${v.emitente_id}` : ''}`);
    },
  });

  const delMut = useMutation({
    mutationFn: async () => {
      if (!vid) return;
      await api.delete(`/veiculos/${vid}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['veiculos'] });
      navigate('/veiculos');
    },
  });

  if (!isNew && q.isLoading) {
    return (
      <div className="py-20 grid place-items-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-3xl mx-auto">
      <header className="rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950/70 dark:shadow-none md:p-5">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="grid h-11 w-11 place-items-center rounded-lg border border-slate-200 bg-white hover:bg-slate-100 dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10 md:h-9 md:w-9"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div>
          <div className="eyebrow">Veículos</div>
          <h1 className="h-display text-xl text-slate-950 mt-0.5 dark:text-white">
            {isNew ? 'Novo veículo' : `Veículo ${form.placa || ''}`}
          </h1>
        </div>
      </div>
      </header>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!form.emitente_id || !form.placa.trim()) return;
          saveMut.mutate(form);
        }}
        className="space-y-4"
      >
        <section className="card-surface p-4 space-y-4 md:p-5">
          <div>
            <h2 className="eyebrow">Dados do veículo</h2>
            <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
              Informações usadas no MDF-e e documentos de transporte.
            </p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:border-slate-800 dark:bg-slate-900/70 dark:text-slate-400">
            Emitente ID: <span className="font-semibold text-slate-950 dark:text-white">{emitenteId || 'selecione no menu superior'}</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <FormField
              label="Placa"
              required
              value={form.placa}
              onChange={(e) => set('placa', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
              maxLength={7}
            />
            <FormField
              label="UF"
              value={form.uf || ''}
              onChange={(e) => set('uf', e.target.value.toUpperCase().slice(0, 2))}
              maxLength={2}
            />
            <FormField
              label="RENAVAM"
              value={form.renavam || ''}
              onChange={(e) => set('renavam', e.target.value.replace(/\D/g, ''))}
              maxLength={11}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <FormField
              label="Tara (kg)"
              type="number"
              value={form.tara ?? ''}
              onChange={(e) => set('tara', e.target.value ? Number(e.target.value) : null)}
            />
            <FormField
              label="Capacidade (kg)"
              type="number"
              value={form.cap_kg ?? ''}
              onChange={(e) => set('cap_kg', e.target.value ? Number(e.target.value) : null)}
            />
            <FormField
              label="Capacidade (m³)"
              type="number"
              value={form.cap_m3 ?? ''}
              onChange={(e) => set('cap_m3', e.target.value ? Number(e.target.value) : null)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="field-label">Tipo de rodado</label>
              <select
                className="field-input"
                value={form.tipo_rodado ?? ''}
                onChange={(e) => set('tipo_rodado', e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">Escolher</option>
                {TIPO_RODADO.map((t) => (
                  <option key={t.v} value={t.v}>
                    {t.l}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="field-label">Tipo de carroceria</label>
              <select
                className="field-input"
                value={form.tipo_carroceria ?? ''}
                onChange={(e) => set('tipo_carroceria', e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">Escolher</option>
                {TIPO_CARROCERIA.map((t) => (
                  <option key={t.v} value={t.v}>
                    {t.l}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <FormField
            label="Descrição"
            value={form.descricao || ''}
            onChange={(e) => set('descricao', e.target.value)}
            maxLength={120}
            placeholder="Ex: Mercedes 1620, ano 2020, carroceria fechada"
          />

          <label className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-700 dark:border-slate-800 dark:bg-slate-900/70 dark:text-slate-300">
            <input
              type="checkbox"
              checked={form.ativo ?? true}
              onChange={(e) => set('ativo', e.target.checked)}
              className="rounded border-slate-300 bg-white text-emerald-600 focus:ring-emerald-500/30 dark:border-slate-700 dark:bg-slate-950"
            />
            <span>Veículo ativo</span>
          </label>
        </section>

        {saveMut.isError && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 text-rose-800 px-3 py-2 text-sm dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300">
            Erro ao salvar.
          </div>
        )}

        <div className="flex flex-col-reverse gap-3 rounded-xl border border-slate-200 bg-white px-3 py-3 shadow-sm dark:border-white/10 dark:bg-slate-950/70 dark:shadow-none sm:flex-row sm:justify-between">
          {!isNew && (
            <button
              type="button"
              onClick={() => {
                if (confirm('Inativar este veículo?')) delMut.mutate();
              }}
              className="btn-danger justify-center text-sm sm:text-xs sm:!py-1.5"
              disabled={delMut.isPending}
            >
              {delMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
              Inativar
            </button>
          )}
          <button
            type="submit"
            disabled={saveMut.isPending || !form.placa.trim() || !form.emitente_id}
            className="btn-primary justify-center text-sm sm:ml-auto sm:text-xs sm:!py-1.5"
          >
            {saveMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Salvar
          </button>
        </div>
      </form>
    </div>
  );
}
